import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import { DragHandleProps } from '@repo/ui/draggable-list'
import { CheckMarkIcon, DeleteIcon, DragIcon, PenIcon, XIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { deleteLanguageTestSection } from '../../../api/language-test/delete-language-test-section'
import { LanguageTestSection } from '../../../lib/types/models/language-test/language-test-section'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../main'

interface LanguageTestSectionCardProps {
  section: LanguageTestSection
  isDragging?: boolean
  dragHandleProps?: DragHandleProps
  isEditing?: boolean
  isUpdating?: boolean

  onClick: (section: LanguageTestSection) => void
  onEdit?: (section: LanguageTestSection) => void
  onUpdate?: (sectionId: number, newLabel: string) => void
  onCancelEdit?: () => void
}

const LanguageTestSectionCard = ({
  section,
  isDragging,
  dragHandleProps,
  isEditing,
  isUpdating,
  onClick,
  onEdit,
  onUpdate,
  onCancelEdit
}: LanguageTestSectionCardProps) => {
  const [editLabel, setEditLabel] = useState(section.label)

  const deleteSectionMutation = useMutation({
    mutationFn: deleteLanguageTestSection,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['language-test-template', section.templateId.toString()]
      })
      toast.success('Section supprimée avec succès')
    },
    onError: showAxiosError
  })

  const handleConfirmEdit = () => {
    if (editLabel.trim() && onUpdate) {
      onUpdate(section.id, editLabel.trim())
    }
  }

  const handleCancelEdit = () => {
    setEditLabel(section.label)
    onCancelEdit?.()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  useEffect(() => {
    if (isEditing) {
      setEditLabel(section.label)
    }
  }, [isEditing, section.label])

  if (isEditing) {
    return (
      <div
        className={`flex items-center gap-3 p-4 bg-white border border-primary-light rounded-lg shadow-sm transition-shadow ${isDragging ? 'shadow-lg opacity-75' : ''}`}
      >
        <div className="flex items-center justify-center w-6 h-6 text-gray-300 cursor-default">
          <DragIcon className="scale-125" />
        </div>

        <div className="flex-1 flex items-center gap-3">
          <div className="flex-1">
            <Input
              type="text"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
              disabled={isUpdating}
            />
          </div>

          <div className="flex items-center gap-2">
            <ActionButton
              variant="danger"
              icon={XIcon}
              onClick={handleCancelEdit}
              disabled={isUpdating}
            />
            <ActionButton
              variant="primary"
              icon={CheckMarkIcon}
              onClick={handleConfirmEdit}
              disabled={isUpdating || !editLabel.trim()}
              isLoading={isUpdating}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex items-center gap-3 p-4 bg-white-hover border border-grey-light rounded-lg shadow-sm hover:shadow-md transition-shadow ${isDragging ? 'shadow-lg opacity-75' : ''}`}
    >
      <div
        {...dragHandleProps}
        {...dragHandleProps?.listeners} // I had to spread the listeners to make the drag handle work
        className="flex items-center justify-center w-6 h-6 text-gray-400 cursor-grab active:cursor-grabbing"
      >
        <DragIcon className="scale-125" />
      </div>

      <div
        className="flex-1 flex items-center justify-between cursor-pointer"
        onClick={() => onClick(section)}
      >
        <div className="flex flex-col gap-2">
          <Typography variant="text" color={tailwindColors.secondary.DEFAULT}>
            {section.label}
          </Typography>
          <Typography variant="label" color={tailwindColors.grey.lighter}>
            {`${section.questions?.length ?? 0} questions`}
          </Typography>
        </div>

        <div className="flex items-center gap-2">
          <ActionButton
            variant="danger"
            icon={DeleteIcon}
            onClick={(e) => {
              e.stopPropagation()
              deleteSectionMutation.mutate(section.id)
            }}
            isLoading={deleteSectionMutation.isPending}
          />
          <ActionButton
            variant="primary"
            icon={PenIcon}
            onClick={(e) => {
              e.stopPropagation()
              onEdit?.(section)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default LanguageTestSectionCard
